import { useMemo, useState } from "react";
import { useListStockInOrders, useListInventoryVendors } from "@workspace/api-client-react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ordId } from "@/lib/display-id";
import { ListPagination } from "@/components/common/list-pagination";
import { StockInOrderForm } from "./stock-in-order-form";
import { StockInOrderDetailSheet } from "./stock-in-order-detail-sheet";
import { ItemCombobox } from "./item-combobox";
import {
  StockInOrderStatus,
  type StockInOrder,
  type StockInOrderStatus as TOrderStatus,
} from "@workspace/api-client-react";

// Phase 22-06 — Orders list tab rendered when mode === "orders" in stock-in-tab.tsx.
// Shows all stock-in orders with pagination + filters + row→detail + New Order button.

const fmtINR = (n: number | string | null | undefined): string => {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num) || num === 0) return n === 0 || num === 0 ? "₹0" : "—";
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

/**
 * Vendor nature badge. Maps inventory_vendors.vendor_type to a 3-state
 * Online / Offline / Service badge so the operator can scan order
 * provenance at a glance.
 *   Online   <- Quick_Commerce, Online_Marketplace
 *   Offline  <- Regular, One_Time
 *   Service  <- Service
 * Unknown / null vendorType renders nothing.
 */
function VendorNatureBadge({ vendorType }: { vendorType: string | null }) {
  if (!vendorType) return null;
  let label: "Online" | "Offline" | "Service" | null = null;
  let cls = "";
  if (vendorType === "Quick_Commerce" || vendorType === "Online_Marketplace") {
    label = "Online"; cls = "bg-sky-50 text-sky-700";
  } else if (vendorType === "Regular" || vendorType === "One_Time") {
    label = "Offline"; cls = "bg-slate-100 text-slate-700";
  } else if (vendorType === "Service") {
    label = "Service"; cls = "bg-purple-50 text-purple-700";
  }
  if (!label) return null;
  return (
    <span className={`rounded px-1.5 py-px text-[10px] font-medium uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

/**
 * Payment status badge.
 *
 * 2026-05-31: switched the comparison from prepaid vs orderTotal (cart math
 * expected value) to prepaid vs the agreed PO total. Reason: operators told us
 * the cart's qty × expected_price math is bookkeeping, not the actual deal —
 * the prepaid amount IS the agreed price. So if anything got paid, the order
 * is Paid. The single nuance is when there's a clear shortfall against the
 * cart math AND the order isn't yet delivered, we hint at Partial.
 *
 *   Not Paid   prepaid is null or 0
 *   Paid       prepaid > 0
 *
 * Kept ultra-simple: any non-zero prepaid reads as Paid. Partial was a source
 * of confusion (ORD-260530-003: prepaid ₹604 was the real price, cart math
 * showed ₹742 because line prices weren't updated — pill flagged Partial
 * incorrectly).
 */
function PaymentStatusPill({
  prepaid,
}: {
  prepaid: number | string | null | undefined;
}) {
  const p = prepaid == null ? 0 : Number(prepaid);
  if (!p || p <= 0) {
    return (
      <span className="inline-flex rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
        Not Paid
      </span>
    );
  }
  return (
    <span className="inline-flex rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
      Paid
    </span>
  );
}

/**
 * Delivered Date cell — date of the latest linked receipt + day delta
 * vs the order date. Helps the operator spot slow deliveries at a glance.
 *   "27/05/2026"     (line 1, latest receipt date)
 *   "+ 6 days"       (line 2, days since order)
 * Renders "—" when no delivery has been recorded yet.
 */
function DeliveredDateCell({
  orderDate,
  deliveredDate,
}: {
  orderDate: string;
  deliveredDate: string | null;
}) {
  if (!deliveredDate) return <span className="text-muted-foreground">—</span>;
  const od = new Date(orderDate);
  const dd = new Date(deliveredDate);
  const days = Math.round((dd.getTime() - od.getTime()) / 86400000);
  return (
    <div className="flex flex-col gap-0.5">
      <span>{dd.toLocaleDateString()}</span>
      <span className="text-xs text-muted-foreground">
        {days === 0 ? "same day" : days > 0 ? `+ ${days} day${days === 1 ? "" : "s"}` : `${days} day${days === -1 ? "" : "s"}`}
      </span>
    </div>
  );
}

/**
 * "Items" column — sum of qty across cart lines, formatted as
 * "delivered / ordered" (units, not line count). Distinct from the
 * line-count Progress column. "—" when no cart lines.
 */
function itemsProgress(order: {
  cartLinesTotal?: number | null;
  cartLinesDelivered?: number | null;
  itemsOrdered?: number | string | null;
  itemsDelivered?: number | string | null;
}): string {
  // 2026-05-31: show cart-line counts (rows), not qty units. Operator's
  // mental model is "6 line items, all delivered" — they don't think of
  // ORS qty=3 as 3 separate items. itemsOrdered/Delivered (qty-based) is
  // still on the response for the order detail; this column just shows
  // line counts.
  const total = order.cartLinesTotal;
  const delivered = order.cartLinesDelivered;
  if (typeof total === "number" && typeof delivered === "number") {
    return total === 0 ? "—" : `${delivered} / ${total}`;
  }
  // Fallback for older API responses lacking the cart-line counts.
  const ordered = order.itemsOrdered;
  const d = order.itemsDelivered;
  if (ordered == null) return "—";
  const o = Number(ordered);
  const dq = Number(d ?? 0);
  if (!Number.isFinite(o) || o === 0) return "—";
  const trim = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, ""));
  return `${trim(dq)} / ${trim(o)}`;
}

const STATUS_PILL: Record<TOrderStatus, string> = {
  Draft: "bg-muted text-muted-foreground",
  Placed: "bg-blue-100 text-blue-700",
  Partially_Delivered: "bg-amber-100 text-amber-700",
  Fully_Delivered: "bg-green-100 text-green-700",
  Closed: "bg-slate-100 text-slate-600",
  Cancelled: "bg-red-100 text-red-600",
};

function OrderStatusPill({ status }: { status: TOrderStatus }) {
  return (
    <span
      className={cn(
        "inline-flex rounded px-1.5 py-0.5 text-xs font-medium",
        STATUS_PILL[status] ?? "bg-muted text-muted-foreground",
      )}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

type PaymentStatusFilter = "Not_Paid" | "Partial" | "Paid";
type VendorNatureFilter = "Online" | "Offline" | "Service";

// Shared filter state — both the StockInOrdersTab list and the
// OrdersFilterBar UI consume this. Hoisted into stock-in-tab.tsx so the
// filter UI can live in the same outer slot as the Receipts filter row.
export interface OrdersFiltersHandle {
  orderDateFrom: string;
  setOrderDateFrom: (v: string) => void;
  orderDateTo: string;
  setOrderDateTo: (v: string) => void;
  itemFilter: number | null;
  setItemFilter: (v: number | null) => void;
  vendorFilter: number | null;
  setVendorFilter: (v: number | null) => void;
  statusFilter: TOrderStatus | "all";
  setStatusFilter: (v: TOrderStatus | "all") => void;
  paymentFilter: PaymentStatusFilter | "all";
  setPaymentFilter: (v: PaymentStatusFilter | "all") => void;
  natureFilter: VendorNatureFilter | "all";
  setNatureFilter: (v: VendorNatureFilter | "all") => void;
  page: number;
  setPage: (v: number) => void;
  pageSize: number;
  setPageSize: (v: number) => void;
  anyFilter: boolean;
  clearFilters: () => void;
}

// Local YYYY-MM-DD helpers — 30-day rolling default for Order date.
const ordYmdLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const ordThirtyDaysAgo = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return ordYmdLocal(d);
};
const ordToday = (): string => ordYmdLocal(new Date());

export function useOrdersFilters(): OrdersFiltersHandle {
  const [orderDateFrom, setOrderDateFrom] = useState(ordThirtyDaysAgo);
  const [orderDateTo, setOrderDateTo] = useState(ordToday);
  const [itemFilter, setItemFilter] = useState<number | null>(null);
  const [vendorFilter, setVendorFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<TOrderStatus | "all">("all");
  const [paymentFilter, setPaymentFilter] = useState<PaymentStatusFilter | "all">("all");
  const [natureFilter, setNatureFilter] = useState<VendorNatureFilter | "all">("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const anyFilter =
    !!orderDateFrom || !!orderDateTo || itemFilter != null || vendorFilter != null ||
    statusFilter !== "all" || paymentFilter !== "all" || natureFilter !== "all";

  function clearFilters() {
    // "Clear" resets dropdowns and dates to their defaults — the 30-day
    // window — not to fully empty. Operator can still widen by editing the
    // date inputs directly.
    setOrderDateFrom(ordThirtyDaysAgo());
    setOrderDateTo(ordToday());
    setItemFilter(null);
    setVendorFilter(null);
    setStatusFilter("all");
    setPaymentFilter("all");
    setNatureFilter("all");
    setPage(1);
  }

  return {
    orderDateFrom, setOrderDateFrom,
    orderDateTo, setOrderDateTo,
    itemFilter, setItemFilter,
    vendorFilter, setVendorFilter,
    statusFilter, setStatusFilter,
    paymentFilter, setPaymentFilter,
    natureFilter, setNatureFilter,
    page, setPage,
    pageSize, setPageSize,
    anyFilter, clearFilters,
  };
}

// Universal-styled filter bar for Orders mode. Rendered by stock-in-tab.tsx
// in the same outer slot as the Receipts filter row.
export function OrdersFilterBar({ filters }: { filters: OrdersFiltersHandle }) {
  const { data: vendors = [] } = useListInventoryVendors();
  const f = filters;
  return (
    <div className="mb-4 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-md border bg-card px-3 py-2.5">
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Order date</p>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label="Order date from"
            value={f.orderDateFrom}
            max={f.orderDateTo || undefined}
            onChange={(e) => { f.setOrderDateFrom(e.target.value); f.setPage(1); }}
            className="h-8 w-[150px]"
          />
          <span className="text-xs text-muted-foreground">→</span>
          <Input
            type="date"
            aria-label="Order date to"
            value={f.orderDateTo}
            min={f.orderDateFrom || undefined}
            onChange={(e) => { f.setOrderDateTo(e.target.value); f.setPage(1); }}
            className="h-8 w-[150px]"
          />
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Item</p>
        <ItemCombobox
          value={f.itemFilter}
          onChange={(id) => { f.setItemFilter(id); f.setPage(1); }}
          showTier={false}
          placeholder="All items"
          className="h-8 w-[200px]"
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Delivery status</p>
        <Select value={f.statusFilter} onValueChange={(v) => { f.setStatusFilter(v as TOrderStatus | "all"); f.setPage(1); }}>
          <SelectTrigger className="h-8 w-[160px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.values(StockInOrderStatus).map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Payment status</p>
        <Select value={f.paymentFilter} onValueChange={(v) => { f.setPaymentFilter(v as PaymentStatusFilter | "all"); f.setPage(1); }}>
          <SelectTrigger className="h-8 w-[140px]"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="Not_Paid">Not Paid</SelectItem>
            <SelectItem value="Partial">Partial</SelectItem>
            <SelectItem value="Paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Vendor nature</p>
        <Select value={f.natureFilter} onValueChange={(v) => { f.setNatureFilter(v as VendorNatureFilter | "all"); f.setPage(1); }}>
          <SelectTrigger className="h-8 w-[140px]"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="Online">Online</SelectItem>
            <SelectItem value="Offline">Offline</SelectItem>
            <SelectItem value="Service">Service</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Vendor</p>
        <Select
          value={f.vendorFilter != null ? String(f.vendorFilter) : "all"}
          onValueChange={(v) => { f.setVendorFilter(v === "all" ? null : Number(v)); f.setPage(1); }}
        >
          <SelectTrigger className="h-8 w-[200px]"><SelectValue placeholder="All vendors" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors
              .filter((v) => v.status !== "blacklisted")
              .map((v) => (
                <SelectItem key={v.id} value={String(v.id)}>{v.vendorName}</SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      {f.anyFilter && (
        <Button variant="ghost" size="sm" onClick={f.clearFilters} className="h-8 text-muted-foreground">
          <X className="mr-1 h-3.5 w-3.5" />
          Clear
        </Button>
      )}
    </div>
  );
}

interface StockInOrdersTabProps {
  filters: OrdersFiltersHandle;
}

export function StockInOrdersTab({ filters }: StockInOrdersTabProps) {
  const {
    orderDateFrom, orderDateTo, itemFilter, vendorFilter,
    statusFilter, paymentFilter, natureFilter, page, pageSize, setPage, setPageSize,
  } = filters;
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Server expects ISO datetimes for dateFrom/dateTo (matches existing convention).
  const dayStartISO = (ymd: string) => new Date(`${ymd}T00:00:00`).toISOString();
  const dayEndISO   = (ymd: string) => new Date(`${ymd}T23:59:59.999`).toISOString();

  const params = {
    page,
    pageSize,
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(vendorFilter != null ? { vendorId: vendorFilter } : {}),
    ...(itemFilter != null ? { itemId: itemFilter } : {}),
    ...(paymentFilter !== "all" ? { paymentStatus: paymentFilter } : {}),
    ...(natureFilter !== "all" ? { vendorNature: natureFilter } : {}),
    ...(orderDateFrom ? { dateFrom: dayStartISO(orderDateFrom) } : {}),
    ...(orderDateTo ? { dateTo: dayEndISO(orderDateTo) } : {}),
  };

  const { data, isLoading } = useListStockInOrders(params);
  const orders = data?.items ?? [];
  const total = data?.total ?? 0;

  const { data: vendors = [] } = useListInventoryVendors();
  const vendorName = useMemo(() => new Map(vendors.map((v) => [v.id, v.vendorName])), [vendors]);

  function handleRowClick(order: StockInOrder) {
    setSelectedOrderId(order.id);
  }

  // Headless @tanstack/react-table for the Purchase Orders list.
  // meta.cellClassName carries per-cell Tailwind for the generic render
  // loop; meta.headClassName likewise for the header cell. Cells reach
  // vendorName + the helper components by closure, same as Receipts.
  type OrderColumn = ColumnDef<StockInOrder, unknown> & {
    meta?: { cellClassName?: string; headClassName?: string };
  };
  const orderColumns = useMemo<OrderColumn[]>(() => [
    {
      id: "order",
      header: "Order",
      cell: ({ row }) => {
        const o = row.original;
        const ref = o.orderId ?? ordId(o.id);
        const isLegacy = ref.startsWith("LEGACY-");
        const displayId = isLegacy ? ref.replace(/^LEGACY-/, "ORD-") : ref;
        return (
          <span className="inline-flex items-center gap-1.5">
            <span>{displayId}</span>
            {isLegacy && (
              <span className="rounded bg-amber-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                Legacy
              </span>
            )}
          </span>
        );
      },
      meta: { cellClassName: "font-mono text-xs font-medium" },
    },
    {
      id: "orderDate",
      header: "Order date",
      cell: ({ row }) => (
        // "Order date" = when payment was actually made (the business-
        // meaningful purchase date). Falls back to createdAt for orders
        // that haven't recorded a payment_date yet (e.g. Draft orders).
        <>{new Date(row.original.paymentDate ?? row.original.createdAt).toLocaleDateString()}</>
      ),
      meta: { cellClassName: "text-sm" },
    },
    {
      id: "deliveryStatus",
      header: "Delivery status",
      cell: ({ row }) => <OrderStatusPill status={row.original.status} />,
    },
    {
      id: "vendor",
      header: "Vendor",
      cell: ({ row }) => {
        const o = row.original;
        return (
          <div className="flex items-center gap-1.5">
            <span>
              {o.vendorId != null
                ? (vendorName.get(o.vendorId) ?? "—")
                : (o.vendorNameFreeText ?? "—")}
            </span>
            <VendorNatureBadge vendorType={(o as { vendorType?: string | null }).vendorType ?? null} />
          </div>
        );
      },
      meta: { cellClassName: "text-sm" },
    },
    {
      id: "items",
      header: "Items",
      cell: ({ row }) => <>{itemsProgress(row.original)}</>,
      meta: { cellClassName: "text-sm text-muted-foreground tabular-nums" },
    },
    {
      id: "totalAmount",
      header: "Total amount",
      cell: ({ row }) => {
        const o = row.original;
        return (
          // Total amount = what was actually paid (prepaidAmount).
          // Falls back to the computed cart total only for Draft
          // orders where no payment has been recorded yet.
          <div className="flex flex-col items-end gap-1">
            <span className="font-mono text-sm tabular-nums">
              {fmtINR(
                o.prepaidAmount ??
                (o as { orderTotal?: number | string | null }).orderTotal ??
                null,
              )}
            </span>
            <PaymentStatusPill prepaid={o.prepaidAmount} />
          </div>
        );
      },
      meta: { cellClassName: "text-right", headClassName: "text-right" },
    },
  ], [vendorName]);

  // useMemo'd row-model factory — see items-tab.tsx for the root-cause
  // writeup of why this can't be a fresh call each render.
  const coreRowModelFactory = useMemo(() => getCoreRowModel(), []);
  const ordersTable = useReactTable({
    data: orders,
    columns: orderColumns,
    getCoreRowModel: coreRowModelFactory,
  });

  return (
    <>
      <Card className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:flex pointer-fine:flex-col">
        <CardHeader className="pointer-fine:shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Purchase Orders</CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                Prepaid split-delivery orders — one upfront payment, multiple shipments.
              </p>
            </div>
            <Button size="sm" onClick={() => setShowCreateForm(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              New Order
            </Button>
          </div>

          {/* Filter UI is rendered in the outer slot by stock-in-tab.tsx so
              Orders mode shares the same universal filter row as Receipts. */}
        </CardHeader>

        <CardContent className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {statusFilter !== "all" || vendorFilter != null
                ? "No orders match the selected filters."
                : "No purchase orders yet. Click New Order to create one."}
            </div>
          ) : (
            // pointer-fine: CardContent scrolls internally + thead pins
            // at top:0 of that. Touch: whole page scrolls, thead inline.
            <table className="w-full caption-bottom text-sm">
              <TableHeader className="pointer-fine:sticky pointer-fine:top-0 pointer-fine:z-10 pointer-fine:bg-background pointer-fine:[&_th]:bg-background">
                {ordersTable.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((header) => {
                      const meta = header.column.columnDef.meta as { headClassName?: string } | undefined;
                      return (
                        <TableHead key={header.id} className={meta?.headClassName}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {ordersTable.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => handleRowClick(row.original)}
                    className="cursor-pointer"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as { cellClassName?: string } | undefined;
                      return (
                        <TableCell key={cell.id} className={meta?.cellClassName}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </table>
          )}

          {!isLoading && total > 0 && (
            <ListPagination
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
              noun={["order", "orders"]}
            />
          )}
        </CardContent>
      </Card>

      {/* Create order form — rendered in a Sheet inside StockInOrderForm */}
      <StockInOrderForm
        open={showCreateForm}
        onOpenChange={setShowCreateForm}
        onSaved={(newOrder) => {
          setShowCreateForm(false);
          setSelectedOrderId(newOrder.id);
        }}
      />

      {/* Order detail drawer */}
      {selectedOrderId != null && (
        <StockInOrderDetailSheet
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </>
  );
}
